import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useTheme } from '../contexts/ThemeContext'
import NotificationsBell from './NotificationsBell'

interface User { id: string; email: string; username: string; role: 'admin' | 'user'; created_at: string }
interface HeaderProps { user: User | null }

const Header: React.FC<HeaderProps> = ({ user }) => {
  const navigate = useNavigate()
  const { isDark, toggleTheme, t, i18n } = useTheme()
  const [menuOpen, setMenuOpen] = useState(false)

  const handleLogout = async () => {
    if (window.confirm(t('logoutConfirm'))) {
      await supabase.auth.signOut()
      navigate('/login')
    }
  }

  const C = {
    bg: isDark
      ? 'linear-gradient(to bottom, rgba(10,10,15,0.98), rgba(15,15,25,0.95))'
      : 'linear-gradient(to bottom, rgba(255,255,255,0.98), rgba(240,244,248,0.95))',
    border: isDark ? 'rgba(229,57,53,0.15)' : 'rgba(229,57,53,0.2)',
    text: isDark ? '#fff' : '#0f172a',
    sub: isDark ? '#888' : '#64748b',
    overlayBg: isDark ? 'rgba(10,10,15,0.99)' : 'rgba(255,255,255,0.99)',
  }

  const btnBase = (): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 6,
    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2vw, 14px)',
    borderRadius: 10, cursor: 'pointer',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    color: isDark ? '#fff' : '#0f172a',
    fontSize: 'clamp(11px, 1.8vw, 14px)', fontWeight: 500,
    transition: 'all 0.2s', outline: 'none',
    whiteSpace: 'nowrap', minHeight: 'clamp(32px, 6vw, 40px)',
  })

  return (
    <>
      <header style={{
        background: C.bg,
        borderBottom: `1px solid ${C.border}`,
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: isDark ? '0 1px 40px rgba(229,57,53,0.08)' : '0 1px 20px rgba(0,0,0,0.08)',
      }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto',
          padding: '0 clamp(12px, 3vw, 24px)',
          height: 'clamp(52px, 8vw, 64px)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div style={{ background: 'linear-gradient(135deg, #e53935, #8e24aa)', width: 'clamp(28px, 5vw, 36px)', height: 'clamp(28px, 5vw, 36px)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 16px rgba(229,57,53,0.4)' }}>
              <span style={{ color: '#fff', fontSize: 'clamp(12px, 2.5vw, 16px)', marginLeft: 2 }}>▶</span>
            </div>
            <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(16px, 3vw, 22px)', fontWeight: 800, color: C.text, letterSpacing: '-0.5px' }}>VidyShort</span>
          </Link>

          {/* Desktop nav */}
          <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: 'clamp(4px, 1vw, 10px)' }}>
            <button onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'sw' : 'en')} style={{ ...btnBase(), gap: 6 }}>
              <span style={{ fontSize: 'clamp(14px, 2vw, 16px)' }}>{i18n.language === 'en' ? '🇹🇿' : '🇬🇧'}</span>
              <span className="nav-label">{i18n.language === 'en' ? 'SW' : 'EN'}</span>
            </button>
            <button onClick={toggleTheme} style={{ ...btnBase(), width: 'clamp(34px, 6vw, 40px)', justifyContent: 'center', padding: '0' }}>
              <span style={{ fontSize: 'clamp(16px, 2.5vw, 18px)' }}>{isDark ? '☀️' : '🌙'}</span>
            </button>
            {user ? (
              <>
                <NotificationsBell userId={user.id} />
                {user.role === 'admin' && (
                  <Link to="/admin" className="nav-label" style={{ ...btnBase(), background: 'linear-gradient(135deg, #8e24aa, #4a148c)', color: '#fff', border: 'none', fontWeight: 700, textDecoration: 'none', boxShadow: '0 2px 12px rgba(142,36,170,0.3)' }}>
                    ⚡ <span className="nav-label">Admin</span>
                  </Link>
                )}
                <Link to="/profile" style={{ ...btnBase(), gap: 8, textDecoration: 'none', color: C.text, fontWeight: 600 }}>
                  <div style={{ width: 'clamp(24px, 4vw, 28px)', height: 'clamp(24px, 4vw, 28px)', borderRadius: '50%', background: 'linear-gradient(135deg, #e53935, #8e24aa)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'clamp(10px, 2vw, 12px)', fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                    {user.username?.charAt(0).toUpperCase()}
                  </div>
                  <span className="nav-label" style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.username}</span>
                </Link>
                <button onClick={handleLogout} className="nav-label" style={{ ...btnBase(), background: 'linear-gradient(135deg, #e53935, #c62828)', color: '#fff', border: 'none', fontWeight: 700, boxShadow: '0 2px 12px rgba(229,57,53,0.3)' }}>
                  {t('logout')}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-label" style={{ ...btnBase(), textDecoration: 'none', color: C.text, fontWeight: 600 }}>{t('signIn')}</Link>
                <Link to="/login?signup=1" className="nav-label" style={{ ...btnBase(), background: 'linear-gradient(135deg, #e53935, #8e24aa)', color: '#fff', border: 'none', fontWeight: 700, textDecoration: 'none', boxShadow: '0 2px 12px rgba(229,57,53,0.3)' }}>{t('signUp')}</Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setMenuOpen(!menuOpen)} className="mobile-menu-btn" style={{ display: 'none', background: 'none', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, borderRadius: 10, color: C.text, fontSize: 'clamp(20px, 4vw, 24px)', cursor: 'pointer', padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2vw, 14px)', minHeight: 'clamp(32px, 6vw, 40px)', minWidth: 'clamp(40px, 8vw, 48px)', alignItems: 'center', justifyContent: 'center', backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }}>
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99, animation: 'fadeIn .2s ease' }} />
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(320px, 85vw)', background: C.overlayBg, backdropFilter: 'blur(30px)', WebkitBackdropFilter: 'blur(30px)', zIndex: 100, padding: 'clamp(16px, 3vw, 24px)', animation: 'slideIn .3s ease', borderLeft: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <button onClick={() => setMenuOpen(false)} style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, borderRadius: 10, color: C.text, fontSize: 18, cursor: 'pointer', padding: '8px 12px', minHeight: 40, minWidth: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            {user && (
              <Link to="/profile" onClick={() => setMenuOpen(false)} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 12, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`, marginBottom: 8 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #e53935, #8e24aa)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{user.username?.charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ color: C.text, fontSize: 'clamp(14px, 2.5vw, 16px)', fontWeight: 700, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.username}</p>
                  <p style={{ color: C.sub, fontSize: 'clamp(11px, 2vw, 13px)', margin: '2px 0 0' }}>{user.role === 'admin' ? '👑 Administrator' : '👤 Member'}</p>
                </div>
              </Link>
            )}
            <Link to="/" onClick={() => setMenuOpen(false)} style={mobileLinkStyle(isDark)}>🏠 Home</Link>
            {user && <>
              <Link to="/history" onClick={() => setMenuOpen(false)} style={mobileLinkStyle(isDark)}>📺 Watch History</Link>
              <Link to="/playlists" onClick={() => setMenuOpen(false)} style={mobileLinkStyle(isDark)}>📋 Playlists</Link>
            </>}
            {user?.role === 'admin' && <Link to="/admin" onClick={() => setMenuOpen(false)} style={{ ...mobileLinkStyle(isDark), color: '#a855f7', border: '1px solid rgba(168,85,247,0.3)', background: isDark ? 'rgba(168,85,247,0.1)' : 'rgba(168,85,247,0.05)' }}>⚡ Admin Panel</Link>}
            <div style={{ height: 1, background: C.border, margin: '8px 0' }} />
            <button onClick={() => { toggleTheme(); setMenuOpen(false) }} style={mobileLinkStyle(isDark)}>{isDark ? '☀️ Light Mode' : '🌙 Dark Mode'}</button>
            <button onClick={() => { i18n.changeLanguage(i18n.language === 'en' ? 'sw' : 'en'); setMenuOpen(false) }} style={mobileLinkStyle(isDark)}>{i18n.language === 'en' ? '🇹🇿 Kiswahili' : '🇬🇧 English'}</button>
            {user
              ? <button onClick={() => { handleLogout(); setMenuOpen(false) }} style={{ ...mobileLinkStyle(isDark), color: '#e53935', border: '1px solid rgba(229,57,53,0.3)', background: 'rgba(229,57,53,0.08)' }}>🚪 {t('logout')}</button>
              : <Link to="/login" onClick={() => setMenuOpen(false)} style={{ ...mobileLinkStyle(isDark), color: '#fff', border: 'none', background: 'linear-gradient(135deg, #e53935, #8e24aa)', textAlign: 'center', justifyContent: 'center' }}>🔐 {t('signIn')} / {t('signUp')}</Link>
            }
          </div>
        </>
      )}

      <style>{`
        @media (max-width: 768px) {
          .mobile-menu-btn { display: flex !important; }
          .desktop-nav     { display: none !important; }
        }
        @media (min-width: 769px) { .mobile-menu-btn { display: none !important; } }
        @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideIn { from { transform: translateX(100%); opacity: 0 } to { transform: translateX(0); opacity: 1 } }
        @media (max-width: 380px) { .nav-label { display: none; } }
      `}</style>
    </>
  )
}

const mobileLinkStyle = (isDark: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: 10,
  width: '100%', padding: '14px 16px', borderRadius: 12,
  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
  background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
  color: isDark ? '#ccc' : '#475569',
  fontSize: 'clamp(14px, 2.5vw, 16px)', fontWeight: 600,
  cursor: 'pointer', textDecoration: 'none', textAlign: 'left',
  transition: 'all .15s', minHeight: 48,
})

export default Header